import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Addon, Category, ShopContent, Subcategory } from '../../../types/cms_shared_types';
import { Step } from '../Step';
import { SubcategoryPicker } from './SubcategoryPicker';
import { SidebarTip } from '../SidebarTip';
import { ProductChoiceList } from './ProductChoiceList';
import { Formik } from 'formik';
import { DefaultBottomBar } from '../BottomBar/DefaultBottomBar';
import { AddonListActions, Product, ProductListActions } from '../../../types/webshop';
import { findSelectedSubcategoryByKey } from '../../../helper/cms';
import { Conditional } from '../../Common/Conditional';

export interface ProductChoiceFormInput {
  selectedSubcategoryKey?: string;
}

interface ProductChoiceProps {
  category: Category;
  selectedSubcategoryKey?: string;
  setSelectedSubcategoryKey: React.Dispatch<React.SetStateAction<string | undefined>>;
  selectedProducts: Product[];
  selectedProductsActions: ProductListActions;
  nextButtonClicked: () => void;
  cmsShopContent: ShopContent;
  selectedAddons: Addon[];
  selectedAddonsActions: AddonListActions;
}

export const ProductChoice: FC<ProductChoiceProps> = ({
  category,
  selectedSubcategoryKey,
  setSelectedSubcategoryKey,
  selectedProducts,
  selectedProductsActions,
  nextButtonClicked,
  cmsShopContent,
  selectedAddons,
  selectedAddonsActions,
}) => {
  const [selectedSubcategory, setSelectedSubcategory] = useState<Subcategory>();

  const monthlyInvoicingAddons = useMemo(
    (): Addon[] => selectedAddons.filter((addon) => addon.addonProvider.forceMonthlyInvoicing),
    [selectedAddons],
  );

  const removeAddons = useCallback(() => {
    monthlyInvoicingAddons.forEach((addon) => {
      const index = selectedAddons.findIndex((selectedAddon) => selectedAddon.code === addon.code);
      selectedAddonsActions.remove(index);
    });
  }, [monthlyInvoicingAddons, selectedAddons, selectedAddonsActions]);

  useEffect(() => {
    const _selectedSubcategory = selectedSubcategoryKey
      ? findSelectedSubcategoryByKey(selectedSubcategoryKey, category.subcategories)
      : undefined;

    if (_selectedSubcategory !== undefined) {
      setSelectedSubcategory(_selectedSubcategory);
      return;
    }

    if (category.subcategories.length > 0) {
      setSelectedSubcategory(category.subcategories[0]);
      setSelectedSubcategoryKey(category.subcategories[0].key);
    }
  }, [category.subcategories, selectedSubcategoryKey, setSelectedSubcategoryKey]);

  return (
    <Formik
      onSubmit={nextButtonClicked}
      initialValues={{
        selectedSubcategoryKey: selectedSubcategoryKey,
      }}
      validate={() => {
        if (selectedProducts.length === 0) {
          return { error: new Error('No products have been selected!') };
        }
      }}
      validateOnMount
      validateOnChange
    >
      {({ submitForm, isValid }) => (
        <Step
          fullWidth
          title={cmsShopContent.productChoiceStep.title}
          content={
            <div className="flex flex-col sm:flex-row sm:justify-center">
              <div className="sm:flex sm:flex-col sm:w-1/4 max-w-[600px]">
                <div className="sm:w-fit sm:flex sm:flex-col sm:self-end">
                  <SubcategoryPicker
                    category={category}
                    selectedProducts={selectedProducts}
                    selectedProductsActions={selectedProductsActions}
                    cmsShopContent={cmsShopContent}
                    selectedSubcategoryKey={selectedSubcategoryKey}
                    setSelectedSubcategoryKey={setSelectedSubcategoryKey}
                  />
                </div>
              </div>

              <div className="flex flex-col items-center sm:max-w-1/2 sm:mx-5 lg:mx-14">
                <Conditional
                  condition={!selectedSubcategory}
                  trueRender={<div>{cmsShopContent.productChoiceStep.pickSubcategoryDescriptionText}</div>}
                  falseRender={
                    <ProductChoiceList
                      selectedSubcategory={selectedSubcategory}
                      selectedProducts={selectedProducts}
                      selectedProductsActions={selectedProductsActions}
                      cmsShopContent={cmsShopContent}
                      monthlyInvoicedAddonsInCart={monthlyInvoicingAddons.length > 0}
                      removeAddons={removeAddons}
                    />
                  }
                />
              </div>

              <div className="w-1/4 max-w-[600px] hidden sm:block">
                <SidebarTip
                  isSecondState={selectedProducts.length > 0}
                  tipTitleBefore={
                    selectedSubcategory?.helpTextTitle ||
                    category.helpTextTitle ||
                    cmsShopContent.productChoiceStep.tipTitleBefore
                  }
                  tipTitleAfter={cmsShopContent.productChoiceStep.tipTitleAfter}
                  tipContentBefore={
                    selectedSubcategory?.helpTextDescription ||
                    category.helpTextDescription ||
                    cmsShopContent.productChoiceStep.tipContentBefore
                  }
                  tipContentAfter={cmsShopContent.productChoiceStep.tipContentAfter}
                />
              </div>
            </div>
          }
          bottomBar={
            <DefaultBottomBar
              continueButtonDisabled={!isValid}
              onClick={submitForm}
              cmsShopContent={cmsShopContent}
              footerContent={cmsShopContent.productChoiceStep.footer}
            />
          }
        />
      )}
    </Formik>
  );
};
